import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	Column,
} from '@tanstack/react-table'
import { RefObject, useMemo } from 'react'
import {
	Delete,
	FilterAltOff,
	InfoOutlined,
	KeyboardArrowDown,
	KeyboardArrowUp,
	RestorePage,
	UnfoldMore,
} from '@mui/icons-material'
import { useMediaQuery } from 'react-responsive'
import { useUserDateFormat } from '../useUserDateFormat'
import { DeletedDocumentData } from '../../lib/api/types/document.interfaces'
import {
	Checkbox,
	EllipsisText,
	IconButton,
	OptionsMenuButton,
	Tooltip,
} from '../../components/ui'
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'
import cn from '../../lib/utils/cn'
import {
	EditingItem,
	DataTableEntry,
	OrderByType,
	OrderDirectionType,
	CustomColumnFilter,
} from '../../pages/Workspace/components/interfaces.table'
import {
	formatDocumentSize,
	shortenFileName,
} from '../../lib/utils/formatDocument'
import FilterButton, {
	FILTERED_OBJECT_TYPE_VALUES,
	FilteredObjectType,
} from '../../components/table/FilterButton'
import FileFormatIcon from '../../components/common/FileFormatIcon'
import { useOutletContext } from 'react-router-dom'
import { MainLayoutType } from '../../components/layouts/MainLayout'

interface useCreateDeletedDataTableProps {
	data?: DeletedDocumentData[] | null
	containerRef?: RefObject<HTMLDivElement>
	onToggleItem?: (
		id: string,
		type: number,
		checked: boolean,
		name?: string,
		fileType?: string
	) => void
	onToggleAll?: (
		ids: { id: string; type: number; name?: string }[],
		checked: boolean
	) => void
	onFileInfo: (id: string) => void
	onRestoreFile: (item: EditingItem) => void
	onDeleteItem: (item: EditingItem) => void
	onChangeFilters: (newFilters: CustomColumnFilter[]) => void
	columnFilters: CustomColumnFilter[]
	checkedItems: {
		id: string
		type: number
	}[]
	sortControllers: {
		orderBy: OrderByType
		orderDirection: OrderDirectionType
		toggleSorting: (columnId: OrderByType) => void
	}
}

type TableColumn = ColumnDef<DataTableEntry, unknown> & {
	accessorKey?: keyof DataTableEntry
}

export const useCreateDeletedDataTable = ({
	data = [],
	containerRef,
	onToggleItem,
	onToggleAll,
	onFileInfo,
	onRestoreFile,
	onDeleteItem,
	onChangeFilters,
	columnFilters,
	checkedItems,
	sortControllers,
}: useCreateDeletedDataTableProps) => {
	const { isApp } = useOutletContext<MainLayoutType>()
	const { formatDate, formatDateTime } = useUserDateFormat()

	const renderSortingHeader = (
		title: string,
		column: Column<DataTableEntry, unknown>,
		className?: string
	) => {
		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 box-border  dark:text-text-table-dark min-h-full',
					className
				)}
				onClick={() => {
					sortControllers?.toggleSorting(column?.id as OrderByType)
				}}
			>
				<FilterButton
					columnKey={column?.id}
					setColumnFilters={onChangeFilters}
					columnFilters={columnFilters}
					isDate={column?.id === 'deletedAt'}
					isSize={column?.id === 'size'}
					objectType={
						FILTERED_OBJECT_TYPE_VALUES?.includes(
							column?.id as FilteredObjectType
						)
							? (column.id as FilteredObjectType)
							: null
					}
				/>
				<EllipsisText
					tooltipLabel={title}
					text={title}
					className="text-center w-fit max-w-auto truncate"
				/>
				<span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5',
						column?.id !== sortControllers.orderBy &&
						'hidden group-hover:inline-block'
					)}
				>
					{column?.id === sortControllers.orderBy &&
						sortControllers?.orderDirection === 'asc' && (
							<KeyboardArrowUp className="w-5 h-5" />
						)}
					{column?.id === sortControllers.orderBy &&
						sortControllers?.orderDirection === 'desc' && (
							<KeyboardArrowDown className="w-5 h-5" />
						)}
					{column?.id !== sortControllers.orderBy && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span>
			</div>
		)
	}

	const tableData = useMemo(() => {
		const safeData = Array.isArray(data) ? data : []
		return safeData?.map((entry) => ({
			id: entry.id,
			name: entry.name,
			type: entry?.fileType
				? entry?.fileType
				: entry?.type
					? 'Folder'
					: entry?.name?.split('.')?.reverse()?.[0]?.toUpperCase(),
			objectType: entry?.type,
			size: entry?.size ?? 0,
			deletedAt: entry?.deletedAt,
			options: {
				id: entry?.id,
			},
		}))
	}, [data])

	const isMobile = useMediaQuery({ maxWidth: 768 })
	const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1024 })

	const columns = useMemo<TableColumn[]>(() => {
		const base: TableColumn[] = [
			{
				accessorKey: 'name',
				enableResizing: true,
				enableSorting: true,
				minSize: 140,
				size: isMobile ? 160 : 200,
				sortingFn: 'alphanumeric',
				header: ({ column }) => {
					const rows = table.getRowModel().rows
					const allIds = rows?.map((row) => ({
						id: row.original.id,
						type: row.original.objectType,
						name: row.original.name,
					}))

					const isAllSelected = Boolean(
						allIds?.length &&
						allIds?.every(({ id }) =>
							checkedItems?.find((item) => item?.id === id)
						)
					)
					return (
						<div className="h-full flex items-center gap-3 max-w-full pl-3 md:pl-6 group cursor-pointer dark:text-text-table-dark">
							<Checkbox
								checked={isAllSelected}
								disabled={!allIds?.length}
								onChange={() => onToggleAll?.(allIds, !isAllSelected)}
							/>
							{renderSortingHeader('Name', column, 'w-fit pl-2')}
						</div>
					)
				},
				cell: ({ row }) => {
					const name = row?.original?.name ?? ''
					const isChecked = checkedItems?.some(
						({ id }) => id === row?.original?.id
					)

					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-table-dark">
							<div
								className="pl-3 md:pl-6 h-14 flex justify-center items-center"
								onClick={(e) => {
									e?.stopPropagation()
									onToggleItem?.(
										row.original.id,
										row.original.objectType,
										!isChecked,
										row?.original?.name,
										row?.original?.type
									)
								}}
							>
								<Checkbox
									checked={isChecked}
									onChange={(_, event) => {
										event?.stopPropagation()
										onToggleItem?.(
											row.original.id,
											row.original.objectType,
											!isChecked,
											row?.original?.name,
											row?.original?.type
										)
									}}
									className="mr-3"
								/>
							</div>
							<FileFormatIcon extension={row?.original?.type} />
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main dark:text-text-table-dark truncate">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'type',
				enableResizing: true,
				minSize: 120,
				size: 120,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				header: ({ column }) => (
					<div className="dark:text-text-table-dark">
						{renderSortingHeader('Type', column)}
					</div>
				),
				cell: (info) => String(info.getValue() ?? ''),
			},
			{
				accessorKey: 'size',
				enableResizing: true,
				minSize: 80,
				size: 80,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				header: ({ column }) => (
					<div className="dark:text-text-table-dark">
						{renderSortingHeader('Size', column)}
					</div>
				),
				cell: (info) =>
					String(formatDocumentSize(Number(info.getValue())) || '-'),
			},
			{
				accessorKey: 'deletedAt',
				enableResizing: true,
				minSize: 130,
				size: 130,
				enableSorting: true,
				sortingFn: 'datetime',
				header: ({ column }) => (
					<div className="dark:text-text-table-dark">
						{renderSortingHeader('Deleted date', column)}
					</div>
				),
				cell: (info) => {
					const val = info.getValue()
					if (!val) return '-'
					const str = String(val)
					return (
						<Tooltip label={formatDateTime(str)} classNameChildrenWrapper="w-fit">
							<span>{formatDate(str)}</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				enableSorting: false,
				size: isMobile ? 100 : columnFilters?.length ? 50 : 30,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-end gap-1 dark:text-text-table-dark min-h-full">
						{!isApp && (
							<IconButton
								variant="transparent"
								className="w-8 h-8 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<ResetColumnsSize className="fill-text-table w-5 h-5 dark:fill-text-table-dark group-hover:fill-accent-hover" />
								}
								onClick={() => table.resetColumnSizing()}
								tooltip={'Reset columns sizes'}
							/>
						)}
						{!!columnFilters?.length && (
							<IconButton
								variant="transparent"
								className="w-8 h-8 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<FilterAltOff className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
								}
								onClick={() => onChangeFilters([])}
								tooltip={'Clean filters'}
							/>
						)}
					</div>
				),
				cell: ({ row }) => {
					const { id, type, name } = {
						id: row.original.id,
						type: row?.original?.objectType,
						name: row?.original?.name,
					}
					return (
						<div className="bg-inherit md:absolute right-0 flex items-center justify-end gap-2 w-fit md:ml-auto pl-4 md:pr-2 h-full group-hover:lg:left-custom-shadow">
							{isMobile ? (
								<OptionsMenuButton
									buttonClassName="ml-auto mr-10"
									parentScrollRef={containerRef}
									items={[
										{
											icon: (
												<InfoOutlined className="w-4 dark:fill-text-main-dark" />
											),
											label: 'File info',
											onClick: () => onFileInfo(id),
										},
										{
											icon: (
												<RestorePage className="w-4 dark:fill-text-main-dark" />
											),
											label: 'Restore',
											onClick: () =>
												onRestoreFile({
													id,
													name,
													type,
												}),
										},
										{
											icon: <Delete className="w-4 dark:fill-text-main-dark" />,
											label: 'Delete',
											onClick: () =>
												onDeleteItem({
													id,
													name,
													type,
												}),
										},
									]}
								/>
							) : (
								<div className="flex items-center justify-center opacity-0 md:opacity-100 gap-2 lg:opacity-0 lg:group-hover:opacity-100 transition-all duration-150 ease-in-out">
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={
											<InfoOutlined className="w-4 dark:fill-text-main-dark" />
										}
										onClick={() => onFileInfo(id)}
										tooltip={type ? 'Folder info' : 'File info'}
									/>
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant={'secondary'}
										icon={
											<RestorePage className="w-4 dark:fill-text-main-dark" />
										}
										tooltip={'Restore'}
										onClick={() =>
											onRestoreFile({
												id,
												name,
												type,
											})
										}
									/>
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={<Delete className="w-4 dark:fill-text-main-dark" />}
										tooltip="Delete"
										onClick={() =>
											onDeleteItem({
												id,
												name,
												type,
											})
										}
									/>
								</div>
							)}
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter((columnDef) => {
				const accessorKey = columnDef.accessorKey as string | undefined
				return (
					accessorKey &&
					[
						'id',
						'name',
						isMobile ? '' : 'type',
						isMobile ? '' : 'size',
						isMobile || isTablet ? '' : 'deletedAt',
						'options',
					].includes(accessorKey)
				)
			})
		}

		return base
	}, [data, checkedItems, columnFilters, isMobile, isTablet])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
	})

	return { table }
}
